import * as Location from 'expo-location';
import React from 'react';

import { logEvent, logWarning } from '@helpers/Loggers';

export type LocationCoordinate = {
  latitude: number;
  longitude: number;
};

export type CurrentLocationCoordinateState = {
  coordinate?: LocationCoordinate;
  loading: boolean;
};

type LocationPermissionsRequestParams = {
  force?: boolean;
};

export function useCurrentLocationCoordinate() {
  const [state, setState] = React.useState<CurrentLocationCoordinateState>({ loading: true });

  React.useEffect(() => {
    let mounted = true;

    getCurrentLocationCoordinate()
      .then((coordinate) => {
        if (mounted) setState({ coordinate, loading: false });
      })
      .catch(() => {
        if (mounted) setState({ loading: false });
      });

    return () => {
      mounted = false;
    };
  }, []);

  return state;
}

export async function getCurrentLocationCoordinate(): Promise<LocationCoordinate | undefined> {
  const { status } = await Location.getForegroundPermissionsAsync();
  if (status !== Location.PermissionStatus.GRANTED) return undefined;

  const location = await Location.getCurrentPositionAsync();
  const coordinate = {
    latitude: location.coords.latitude,
    longitude: location.coords.longitude,
  };

  return isValidLocationCoordinate(coordinate) ? coordinate : undefined;
}

export async function requestForegroundLocationPermissions({
  force,
}: LocationPermissionsRequestParams = {}) {
  const { status: currentStatus } = await Location.getForegroundPermissionsAsync();
  if (currentStatus === Location.PermissionStatus.UNDETERMINED || force) {
    const { status: newStatus } = await Location.requestForegroundPermissionsAsync();
    logEvent('foreground_location_permissions', { status: newStatus });
  } else {
    logWarning('Not requesting background location permissions (determined)');
  }
}

export async function requestBackgroundLocationPermissions({
  force,
}: LocationPermissionsRequestParams = {}) {
  const isAvailable = await Location.isBackgroundLocationAvailableAsync();
  if (isAvailable) {
    const { status: currentStatus } = await Location.getBackgroundPermissionsAsync();
    if (currentStatus === Location.PermissionStatus.UNDETERMINED || force) {
      const { status: newStatus } = await Location.requestBackgroundPermissionsAsync();
      logEvent('background_location_permissions', { status: newStatus });
    } else {
      logWarning('Not requesting background location permissions (determined)');
    }
  } else {
    logWarning('Not requesting background location permissions (unavailable)');
  }
}

function isValidLocationCoordinate(coordinate?: Partial<LocationCoordinate>) {
  return (
    typeof coordinate?.latitude === 'number' &&
    Number.isFinite(coordinate.latitude) &&
    typeof coordinate?.longitude === 'number' &&
    Number.isFinite(coordinate.longitude)
  );
}
